import React, {
	useContext,
 useEffect, useState
} from 'react';
import {
 PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import {
 Link, useLocation, Location
} from 'react-router-dom';
import {
 getRoleAccessLevel
} from 'shared/utils/organizationUtils';
import {
 OrganizationContext
} from 'shared/context/OrganizationContext';
import {
 cn
} from 'shared/lib/utils';

// ✅ Define types for the section prop
interface SectionItem {
	path: string;
	name: string;
	accessRole?: 'superAdmin' | 'admin' | 'editor';
	icon: React.ReactNode;
}

interface Section {
	name?: string;
	list: SectionItem[];
}

interface SideMenuProps {
	section: Section;
}

export default function SideMenu({
 section
}: SideMenuProps) {
	const [isOpen, setIsOpen] = useState<boolean>(true);

	// ✅ useLocation already returns a typed Location object
	const route: Location = useLocation();
	const {
		pathname
	} = route;
	const {
		selectedOrganization
	} = useContext(OrganizationContext);

	useEffect(() => {
		const handleResize = () => {
			const currentWidth = window.innerWidth;
			if (currentWidth < 900) {
				setIsOpen(false);
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize(); // Call once on mount

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	function checkAccess(role?: 'superAdmin' | 'admin' | 'editor') {
		if (!role) return true;
		// Placeholder for access control logic
		const accessLevel = getRoleAccessLevel(role);
		const userAccessLevel = getRoleAccessLevel(selectedOrganization?.userRole);
		if (userAccessLevel < accessLevel) return false;
		return true;
	}

	const segments = pathname.split('/').filter(Boolean);

	const getRoute = (path: string) => {
		// Index entry (e.g. "Studios" at the org root): active only when no
		// sibling section segment is present in the URL.
		if (path === '.') {
			const siblings = section.list
				.map((i) => i.path)
				.filter((p) => p !== '.' && !p.includes('/'));
			return !siblings.some((p) => segments.includes(p));
		}
		// Absolute path: exact match or a descendant of it.
		if (path.includes('/')) {
			return pathname === path || pathname.startsWith(`${path}/`);
		}
		// Relative single-segment path: active when it appears as a URL segment.
		return segments.includes(path);
	};

	return (
    <div
		className={`bg-gray-900 text-white ${
			isOpen ? 'w-[50px] md:w-64 md:min-w-64 p-6' : 'w-[50px] py-6 px-4'
		} transition-all flex flex-col gap-6`}
		>
        <nav className="flex flex-col gap-1">
            <span className={cn(
				'mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 truncate',
				!isOpen && 'text-center'
			)}>
                {isOpen ? section.name || '-' : '-'}
            </span>

            {section.list.filter((item) => checkAccess(item.accessRole)).map((item) => {
				const active = getRoute(item.path);
				return (
					<Link
						key={item.path}
						to={item.path}
						title={!isOpen ? item.name : undefined}
						className={cn(
							'group relative flex items-center gap-3 rounded-lg py-2 text-sm transition-colors',
							isOpen ? 'px-3' : 'px-0 justify-center',
							active
								? 'bg-white/10 text-white font-medium'
								: 'text-gray-400 hover:bg-white/5 hover:text-white'
						)}
					>
						{active && (
							<span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary" />
						)}
						<span className="shrink-0">{item.icon}</span>
						{isOpen && <span className="truncate">{item.name}</span>}
					</Link>
				);
			})}
        </nav>

        <div className={cn(
			'mt-auto hidden border-t border-white/10 pt-3 md:flex',
			isOpen ? 'justify-end' : 'justify-center'
		)}>
            <button
				type="button"
				onClick={() => setIsOpen((prev) => !prev)}
				title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
				aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
				className="flex items-center justify-center rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
				>
                {isOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            </button>
        </div>
    </div>
	);
}
